/* DSCR Calculator — Interactive Math, URL Sync, Charts & CSV Export */

let dscrDonutChart = null;
let dscrProjectionChart = null;

function getDscrInputs() {
  const getVal = (id, def = 0) => {
    const el = document.getElementById(id);
    if (!el) return def;
    const v = +el.value;
    return isNaN(v) ? def : v;
  };

  return {
    noi: getVal('dscrNoi', 150000),
    debt: getVal('dscrDebt', 100000),
    growth: getVal('dscrGrowth', 3),
    years: getVal('dscrYears', 5),
  };
}

function updateDscrUrlParams(params) {
  try {
    const url = new URL(window.location);
    url.searchParams.set('noi', params.noi);
    url.searchParams.set('debt', params.debt);
    if (params.growth !== 3) {
      url.searchParams.set('growth', params.growth);
    } else {
      url.searchParams.delete('growth');
    }
    window.history.replaceState({}, '', url);
  } catch (e) {}
}

function parseDscrUrlParams() {
  const searchParams = new URLSearchParams(window.location.search);
  const noi = searchParams.get('noi');
  const debt = searchParams.get('debt');
  const growth = searchParams.get('growth');

  return {
    noi: noi !== null ? +noi : null,
    debt: debt !== null ? +debt : null,
    growth: growth !== null ? +growth : null,
  };
}

function calculateDscr(params) {
  const { noi, debt, growth, years } = params;

  const ratio = debt > 0 ? noi / debt : 0;
  const dscr = Math.round(ratio * 100) / 100;
  const netCashFlow = noi - debt;

  let status = 'strong';
  let badgeText = 'Strong Coverage (≥ 1.25)';
  let headlineText = `Healthy DSCR of ${dscr.toFixed(2)}x`;
  let detailText = `Net Operating Income exceeds debt service by ${fmt(netCashFlow)} per year. Meets standard commercial bank lending approval benchmarks.`;

  if (dscr < 1.0) {
    status = 'shortfall';
    badgeText = 'Deficit / Shortfall (< 1.00)';
    headlineText = `Cash Shortfall of ${fmt(Math.abs(netCashFlow))}/yr`;
    detailText = `Warning: Operating income is insufficient to cover annual debt service obligations. Property/business operates at a net cash deficit.`;
  } else if (dscr < 1.25) {
    status = 'moderate';
    badgeText = 'Tight Buffer (1.00 – 1.24)';
    headlineText = `Tight DSCR Coverage of ${dscr.toFixed(2)}x`;
    detailText = `Income covers debt payments with a surplus of ${fmt(netCashFlow)}/yr, but buffers are narrow. Lenders may require higher debt service reserves.`;
  }

  const schedule = [];
  let currentNoi = noi;

  for (let yr = 1; yr <= years; yr++) {
    const yrDscr = debt > 0 ? Math.round((currentNoi / debt) * 100) / 100 : 0;
    const yrSurplus = currentNoi - debt;

    schedule.push({
      year: yr,
      noi: Math.round(currentNoi),
      debtService: Math.round(debt),
      dscr: yrDscr,
      surplus: Math.round(yrSurplus),
    });

    currentNoi *= 1 + growth / 100;
  }

  return {
    dscr,
    netCashFlow,
    status,
    badgeText,
    headlineText,
    detailText,
    schedule,
  };
}

function updateDisplays(params) {
  const setTxt = (id, txt) => {
    const el = document.getElementById(id);
    if (el) el.textContent = txt;
  };

  setTxt('dscrNoiDisplay', fmt(params.noi));
  setTxt('dscrDebtDisplay', fmt(params.debt));
  setTxt('dscrGrowthDisplay', `${params.growth}%/yr`);
  setTxt('dscrYearsDisplay', `${params.years} yrs`);
}

function renderDscrResults(result, params) {
  const setTxt = (id, txt) => {
    const el = document.getElementById(id);
    if (el) el.textContent = txt;
  };

  const card = document.getElementById('dscrVerdictCard');
  if (card) {
    card.classList.remove('dscr-strong', 'dscr-moderate', 'dscr-shortfall');
    card.classList.add(`dscr-${result.status}`);
  }

  setTxt('dscrBadge', result.badgeText);
  setTxt('dscrHeadline', result.headlineText);
  setTxt('dscrDetail', result.detailText);

  setTxt('dscrRatioValue', `${result.dscr.toFixed(2)}x`);
  setTxt('dscrCashFlowValue', (result.netCashFlow >= 0 ? '+' : '') + fmt(result.netCashFlow));
  setTxt('dscrNoiValue', fmt(params.noi));
  setTxt('dscrDebtValue', fmt(params.debt));

  renderTable(result.schedule);

  try {
    if (typeof Chart !== 'undefined') {
      renderDonutChart(params.noi, params.debt);
      renderProjectionChart(result.schedule);
    }
  } catch (err) {
    console.warn('DSCR Chart rendering error:', err);
  }
}

function renderDonutChart(noi, debt) {
  const ctx = document.getElementById('dscrDonutChart');
  if (!ctx) return;

  if (dscrDonutChart) dscrDonutChart.destroy();

  const operatingMargin = Math.max(0, noi - debt);

  dscrDonutChart = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: ['Annual Debt Service', 'Net Cash Cushion'],
      datasets: [
        {
          data: [debt, operatingMargin],
          backgroundColor: [cssVar('--chart-rent'), cssVar('--chart-buy')],
          borderColor: cssVar('--bg-card'),
          borderWidth: 3,
          hoverOffset: 4,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: { color: cssVar('--chart-tick'), usePointStyle: true, pointStyle: 'circle', padding: 16 },
        },
        tooltip: {
          backgroundColor: cssVar('--tooltip-bg'),
          borderColor: cssVar('--tooltip-border'),
          borderWidth: 1,
          titleColor: cssVar('--tooltip-title'),
          bodyColor: cssVar('--tooltip-body'),
          callbacks: {
            label: (ctx) => `${ctx.label}: ${fmt(ctx.raw)}`,
          },
        },
      },
      cutout: '72%',
    },
  });
}

function renderProjectionChart(schedule) {
  const ctx = document.getElementById('dscrProjectionChart');
  if (!ctx) return;

  const labels = schedule.map((r) => `Yr ${r.year}`);
  const noiData = schedule.map((r) => r.noi);
  const debtData = schedule.map((r) => r.debtService);

  if (dscrProjectionChart) dscrProjectionChart.destroy();

  dscrProjectionChart = new Chart(ctx, {
    type: 'bar',
    data: {
      labels,
      datasets: [
        {
          label: 'Net Operating Income (NOI)',
          data: noiData,
          backgroundColor: cssVar('--chart-buy'),
          borderRadius: 6,
        },
        {
          label: 'Annual Debt Service',
          data: debtData,
          backgroundColor: cssVar('--chart-rent'),
          borderRadius: 6,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          position: 'top',
          align: 'end',
          labels: { color: cssVar('--chart-tick'), usePointStyle: true, pointStyle: 'circle', padding: 16 },
        },
        tooltip: {
          backgroundColor: cssVar('--tooltip-bg'),
          borderColor: cssVar('--tooltip-border'),
          borderWidth: 1,
          titleColor: cssVar('--tooltip-title'),
          bodyColor: cssVar('--tooltip-body'),
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.raw)}`,
          },
        },
      },
      scales: {
        x: {
          grid: { color: cssVar('--chart-grid') },
          ticks: { color: cssVar('--chart-tick') },
        },
        y: {
          grid: { color: cssVar('--chart-grid') },
          ticks: {
            color: cssVar('--chart-tick'),
            callback: (v) => fmtCompact(v),
          },
        },
      },
    },
  });
}

function renderTable(schedule) {
  const tbody = document.getElementById('dscrScheduleBody');
  if (!tbody) return;

  tbody.innerHTML = schedule.map((row) => {
    const isSurplus = row.surplus >= 0;
    const advClass = isSurplus ? 'advantage-renter' : 'advantage-buyer';
    const advLabel = isSurplus ? `+${fmt(row.surplus)}` : fmt(row.surplus);

    return `
    <tr>
      <td>Year ${row.year}</td>
      <td>${fmt(row.noi)}</td>
      <td>${fmt(row.debtService)}</td>
      <td style="font-weight: 700;">${row.dscr.toFixed(2)}x</td>
      <td class="${advClass}">${advLabel}</td>
    </tr>`;
  }).join('');
}

function exportDscrCsv() {
  const params = getDscrInputs();
  const result = calculateDscr(params);
  const headers = ['Year', 'Net Operating Income', 'Annual Debt Service', 'DSCR Ratio', 'Net Surplus/Shortfall'];
  const rows = result.schedule.map((r) => [
    r.year, r.noi, r.debtService, r.dscr, r.surplus
  ]);

  const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `dscr-coverage-schedule-${params.years}yrs.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function applyDscrCountryRanges(country, overrideParams = {}) {
  if (!country) return;
  const curr = country.currency || 'USD';

  let defaultNoi = 150000;
  let defaultDebt = 100000;
  let maxVal = 2000000;
  let stepVal = 10000;

  if (curr === 'INR') {
    defaultNoi = 15000000;
    defaultDebt = 10000000;
    maxVal = 100000000;
    stepVal = 500000;
  } else if (curr === 'JPY') {
    defaultNoi = 20000000;
    defaultDebt = 15000000;
    maxVal = 200000000;
    stepVal = 1000000;
  }

  const noiSlider = document.getElementById('dscrNoi');
  if (noiSlider) {
    noiSlider.max = maxVal;
    noiSlider.step = stepVal;
    noiSlider.value = overrideParams.noi ?? defaultNoi;
  }

  const debtSlider = document.getElementById('dscrDebt');
  if (debtSlider) {
    debtSlider.max = maxVal;
    debtSlider.step = stepVal;
    debtSlider.value = overrideParams.debt ?? defaultDebt;
  }
}

function recalculateDscr() {
  const params = getDscrInputs();
  updateDisplays(params);
  updateDscrUrlParams(params);
  const result = calculateDscr(params);
  renderDscrResults(result, params);
}

function initDscrCalculator() {
  if (!document.getElementById('dscrNoi')) return;

  const urlParams = parseDscrUrlParams();

  initCountry((country) => {
    applyDscrCountryRanges(country, urlParams);
    recalculateDscr();
  });

  if (typeof currentCountry !== 'undefined') {
    applyDscrCountryRanges(currentCountry, urlParams);
  }

  if (urlParams.noi !== null && document.getElementById('dscrNoi')) {
    document.getElementById('dscrNoi').value = urlParams.noi;
  }
  if (urlParams.debt !== null && document.getElementById('dscrDebt')) {
    document.getElementById('dscrDebt').value = urlParams.debt;
  }
  if (urlParams.growth !== null && document.getElementById('dscrGrowth')) {
    document.getElementById('dscrGrowth').value = urlParams.growth;
  }

  const inputs = document.querySelectorAll('input[type="range"]');
  inputs.forEach((el) => el.addEventListener('input', recalculateDscr));

  const exportBtn = document.getElementById('exportDscrCsv');
  if (exportBtn) exportBtn.addEventListener('click', exportDscrCsv);

  recalculateDscr();
}

document.addEventListener('DOMContentLoaded', initDscrCalculator);
